import clientAuth from "../clientAuth";
import client from "../client";
import axios from "axios";

// Cache for user data
let userDataCache = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache object for UserService
const serviceCache = {
  patients: {
    data: null,
    timestamp: null,
    params: null,
    CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
  },
  appointments: {
    data: null,
    timestamp: null,
    params: null,
    CACHE_DURATION: 2 * 60 * 1000, // 2 minutes for appointments (more frequent updates)
  }
};

// Helper function to check if cache is valid
const isCacheValid = (cache, params) => {
  if (!cache.data || !cache.timestamp || !cache.params) return false;
  
  const isExpired = Date.now() - cache.timestamp > cache.CACHE_DURATION;
  if (isExpired) return false;

  // Compare params to ensure we're not using cached data for different filters
  const paramsMatch = JSON.stringify(cache.params) === JSON.stringify(params);
  return paramsMatch;
};

// Function to clear user data cache
const clearUserDataCache = () => {
  console.log('Clearing user data cache');
  userDataCache = null;
  lastFetchTime = 0;
  
  // Also clear other service caches
  Object.keys(serviceCache).forEach(key => {
    serviceCache[key] = {
      data: null,
      timestamp: null,
      params: null,
      CACHE_DURATION: serviceCache[key].CACHE_DURATION
    };
  });
};

const getNonAdminDashboardData = async () => {
  try {
    const response = await clientAuth.get('non-admin/dashboard').json();
    console.log('Fetched non-admin dashboard data:', response);
    return {
      status: true,
      data: response
    };
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    return {
      status: false,
      error: error.message
    };
  }
};

const getPatientCategories = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/patient-categories`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    const data = await response.json();
    console.log('Fetched patient categories:', data);
    return {
      status: true,
      data: data
    };
  } catch (error) {
    console.error('Error fetching patient categories:', error);
    return {
      status: false,
      error: error.message
    };
  }
};

const getDoctorDashboardData = async (doctorId = null) => {
  try {
    const endpoint = doctorId
      ? `session/doctor/overview-metrics?doctor_id=${doctorId}`
      : 'session/doctor/overview-metrics';
    const response = await clientAuth.get(endpoint).json();
    return {
      status: true,
      data: response
    };
  } catch (error) {
    return {
      status: false,
      error: error.message
    };
  }
};

const getPatients = async (params) => {
  try {
    // Check cache first
    const now = Date.now();
    const cache = serviceCache.patients;
    
    if (cache.data && 
        cache.timestamp && 
        (now - cache.timestamp) < cache.CACHE_DURATION &&
        JSON.stringify(cache.params) === JSON.stringify(params)) {
      return cache.data;
    }

    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.perPage || 10,
      search: params.search || '',
      status: params.status || 'all',
      sortField: params.sortField || 'next_appointment_date',
      sortDirection: params.sortDirection || 'asc'
    });

    if (params.doctorId) {
      queryParams.set('doctor_id', params.doctorId);
    }

    const response = await clientAuth.get(`session/patients?${queryParams}`).json();
    
    if (!response) {
      throw new Error('No response from server');
    }

    const result = {
      status: true,
      data: {
        patients: response.data?.patients || [],
        total: response.data?.total || 0
      }
    };

    // Update cache
    serviceCache.patients.data = result;
    serviceCache.patients.timestamp = now;
    serviceCache.patients.params = { ...params };

    return result;
  } catch (error) {
    console.error('Error fetching patients:', error);
    return {
      status: false,
      error: error.message,
      data: {
        patients: [],
        total: 0
      }
    };
  }
};

const getAppointments = async (params = {}) => {
  try {
    // Check cache first
    if (isCacheValid(serviceCache.appointments, params)) {
      console.log('Using cached appointments data');
      return serviceCache.appointments.data;
    }

    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.perPage || 10,
      sort_field: params.sort_field || 'appt_date',
      sort_direction: params.sort_direction || 'asc',
      search: params.search || '',
      start_date: params.start_date || '',
      end_date: params.end_date || '',
      date: params.date || '',
      status: params.status || '',
      patient_id: params.patient_id || ''
    });

    if (params.doctorId) {
      queryParams.set('doctor_id', params.doctorId);
    }

    const response = await clientAuth.get(`session/appointments?${queryParams}`);
    const data = await response.json();
    
    if (data.success) {
      const responseData = {
        status: true,
        data: {
          appointments: data.data || [],
          pagination: {
            current_page: data.current_page || 1,
            total: data.total || 0,
            per_page: data.per_page || 10,
            last_page: data.last_page || 1
          }
        }
      };

      // Update cache
      serviceCache.appointments = {
        data: responseData,
        timestamp: Date.now(),
        params: params,
        CACHE_DURATION: serviceCache.appointments.CACHE_DURATION
      };

      return responseData;
    } else {
      throw new Error(data.message || 'Failed to fetch appointments');
    }
  } catch (error) {
    console.error('Error fetching appointments:', error);
    return {
      status: false,
      error: error.message,
      data: {
        appointments: [],
        pagination: {
          current_page: 1,
          total: 0,
          per_page: 10,
          last_page: 1
        }
      }
    };
  }
};

// Add a function to clear appointments cache when needed
const clearAppointmentsCache = () => {
  serviceCache.appointments = {
    data: null,
    timestamp: null,
    params: null,
    CACHE_DURATION: serviceCache.appointments.CACHE_DURATION
  };
};

// Add a function to clear patients cache when needed
const clearPatientsCache = () => {
  serviceCache.patients = {
    data: null,
    timestamp: null,
    params: null,
    CACHE_DURATION: serviceCache.patients.CACHE_DURATION
  };
};

/**
 * Fetch all doctors (id, name, npi_id)
 */
const getDoctors = async () => {
    try {
        const response = await clientAuth.get('doctors');
        const data = await response.json();
        return { status: true, data };
    } catch (error) {
        console.error('Error fetching doctors');
        return { status: false, error: error.message };
    }
};

/**
 * Update user's NPI ID
 * @param {string} userId
 * @param {Array|string} npi_id - Single NPI ID or array of NPI IDs
 */
const updateUserNpi = async (userId, npi_id) => {
  try {
    const npiArray = npi_id && npi_id.length > 0 ? (Array.isArray(npi_id) ? npi_id : [npi_id]) : null;
    
    const response = await clientAuth.put(`users/${userId}/npi`, { 
      json: { npi_id: npiArray } 
    }).json();
    return response;
  } catch (error) {
    console.error('Error updating user NPI');
    throw error;
  }
};

const logUserNpiMapping = (user, doctors) => {
    if (!user.npi_id || (Array.isArray(user.npi_id) && user.npi_id.length === 0)) {
        return [];
    }
    
    let npiArray;
    if (typeof user.npi_id === 'string' && user.npi_id.startsWith('[')) {
        try {
            npiArray = JSON.parse(user.npi_id);
        } catch (e) {
            console.error('Failed to parse NPI string');
            return [];
        }
    } else {
        npiArray = Array.isArray(user.npi_id) ? user.npi_id : [user.npi_id];
    }
    
    npiArray = [...new Set(npiArray.map(npi => String(npi)))];
    
    const mappedOptions = npiArray.map(npi => {
        const found = doctors.find(doc => String(doc.npi_id) === npi);
        
        return found ? {
            value: npi,
            label: `${found.name} (${npi})${found.address ? ' - ' + found.address : ''}${found.city ? ', ' + found.city : ''}`
        } : null;
    }).filter(Boolean);
    
    return mappedOptions;
};

export default {
  login: (userData) => client.post('session/login', { json: userData }),
  userlogout: async (userData) => {
    clearUserDataCache();
    return clientAuth.post('session/logout', { json: userData });
  },
  forgot_pwd: (userData) => client.post('session/forgot-password', { json: userData }),
  profileupdate: (userData) => clientAuth.post('session/admin/updateprofile', { json: userData }),
  GetUser: async (doctorId = null) => {
    // Check cache first
    const now = Date.now();
    const role = localStorage.getItem("Infuseonerolename");
    const token = localStorage.getItem("InfuseoneUserToken");
    
    console.log('GetUser called with role:', role);
    console.log('Current cache state:', { 
      hasCache: !!userDataCache, 
      lastFetchTime, 
      timeSinceLastFetch: now - lastFetchTime,
      cacheDuration: CACHE_DURATION
    });
    
    // If no token, clear cache and return
    if (!token) {
      console.log('No token found, clearing cache');
      clearUserDataCache();
      return {
        status: false,
        error: 'No authentication token found'
      };
    }
    
    // Check if cache is valid
    if (!doctorId && userDataCache && (now - lastFetchTime) < CACHE_DURATION) {
      // Verify the cached data matches the current role
      const cachedRole = userDataCache.role || userDataCache.role_name;
      if (cachedRole === role) {
        console.log('Using valid cached user data for role:', role);
        return {
          status: true,
          data: userDataCache
        };
      } else {
        console.log('Role mismatch - cached role:', cachedRole, 'current role:', role);
        clearUserDataCache();
      }
    }

    try {
      console.log('Fetching fresh user data for role:', role);
      // Use different endpoints based on role
      const endpoint = role === "Admin"
        ? 'session/admin/show'
        : doctorId
          ? `session/user/show?doctor_id=${doctorId}`
          : 'session/user/show';
      const response = await clientAuth.get(endpoint).json();
      
      console.log('User data response:', response);
      
      if (!response) {
        throw new Error('No response from server');
      }
      
      // Update cache
      if (!doctorId) {
        userDataCache = response;
        lastFetchTime = now;
      }
      
      return {
        status: true,
        data: response
      };
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Clear cache on error
      clearUserDataCache();
      return {
        status: false,
        error: error.message || 'Failed to fetch user data'
      };
    }
  },
  UserchangePassword: (userData) => clientAuth.post('session/admin/change-password', { json: userData }),

  // Get User list
  GetUserList: (page = 1, userData) => {
    const endpoint = `admin/users/index?page=${page}&per_page=10`;
    return clientAuth.post(endpoint, { json: userData });
  },
  
  ChangeUserStatus: (userId) => clientAuth.post(`admin/users/active/deactive/${userId}`),
  DeleteUser: (userId) => clientAuth.delete(`admin/users/destroy/${userId}`),
  AddUser: (userData) => clientAuth.post('admin/users/store', { json: userData }),
  GetUserById: (userId) => clientAuth.get(`admin/users/show/${userId}`),
  UpdateUser: (userId, formData) => clientAuth.post(`admin/users/update/${userId}`, { json: formData }),
  GetDoctorGroups: () => clientAuth.get('admin/doctor-groups'),
  GetDoctorGroupOptions: () => clientAuth.get('admin/doctor-groups/options'),
  GetDoctorGroupMemberOptions: () => clientAuth.get('admin/doctor-groups/member-options'),
  CreateDoctorGroup: (groupData) => clientAuth.post('admin/doctor-groups', { json: groupData }),
  UpdateDoctorGroup: (groupId, groupData) => clientAuth.put(`admin/doctor-groups/${groupId}`, { json: groupData }),

  AssigneDrugIdUser: (userId, userData) => clientAuth.post(`admin/users/assigne/drug/${userId}`, { json: userData }),
  AssignedDrugIdlist: (userId) => clientAuth.get(`admin/users/assigned/drug/list/${userId}`),

  // User register 
  NewUserRegister: (userData) => client.post('session/user/register', { json: userData }),

  // session/user/drug/list 
  GetUserAssignedDrugList: (doctorId) => clientAuth.get(
    doctorId ? `session/user/drug/list?doctor_id=${doctorId}` : 'session/user/drug/list'
  ),

  // session/user/show/drug/form  
  ShowUserdrugDetailForm: (drugId, doctorId) => clientAuth.get(
    doctorId
      ? `session/user/show/drug/form/${drugId}?doctor_id=${doctorId}`
      : `session/user/show/drug/form/${drugId}`
  ),

  GetAccessibleDoctors: () => clientAuth.get('session/user/accessible-doctors'),

  // search user 18-03-25
  searchUsers: (userData) => clientAuth.post('admin/users/search', { json: userData }),

  // Dashboard metrics
  getDashboardMetrics: async () => {
    try {
      const role = localStorage.getItem("Infuseonerolename");
      const endpoint = role === "Admin" ? 'admin/dashboard-metrics' : 'session/doctor/overview-metrics';
      const response = await clientAuth.get(endpoint);
      const data = await response.json();
      return data;
    } catch (error) {
      throw new Error('Failed to fetch dashboard metrics');
    }
  },
  
  // total/assigned/drug
  totalAssignedDrugs: (doctorId) => clientAuth.get(
    doctorId ? `session/total/assigned/drug?doctor_id=${doctorId}` : `session/total/assigned/drug`
  ),

  // notification
  AllNotificationList: () => clientAuth.get(`notification/notification-list`),

  // notification/unread-notification-count
  NotificationUnreadCount: () => clientAuth.get(`notification/unread-notification-count`),

  // notification/mark-all-read
  NotificationMarkallread: () => clientAuth.get(`notification/mark-all-read`),
  GetFormData: (formId) => clientAuth.get(`form-mapping/${formId}`),
  GetUserForForm: (doctorId) => clientAuth.get(
    doctorId ? `session/user/show?doctor_id=${doctorId}` : `session/user/show`
  ),
  getAppointments,
  getNonAdminDashboardData,
  getPatientCategories,
  getDoctorDashboardData,
  getPatients,
  getDoctors,
  updateUserNpi,
  clearAppointmentsCache,
  clearPatientsCache,
  logUserNpiMapping
};
